import pulseHeroImg from "@/assets/pulsechat_vibe.jpg";
import { ChevronDown } from "lucide-react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const Hero = () => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x);
  const mouseYSpring = useSpring(y);

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["10deg", "-10deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-10deg", "10deg"]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <section
      id="home"
      className="pt-24 pb-16 flex flex-col items-center justify-center relative overflow-hidden"
      style={{ background: "var(--gradient-hero)" }}
    >
      {/* Background decoration with pulse and grid */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.05, 0.15, 0.05]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/3 right-1/3 w-96 h-96 rounded-full bg-pulse/20 blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.03, 0.1, 0.03]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-1/3 left-1/4 w-64 h-64 rounded-full bg-electric/10 blur-3xl"
        />
      </div>

      <div className="max-w-5xl mx-auto px-6 w-full flex-1 flex items-center">
        <div className="grid md:grid-cols-2 gap-12 items-center w-full">
          {/* Left: Text */}
          <div className="order-2 md:order-1">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-body text-sm tracking-[0.3em] uppercase text-pulse mb-4"
            >
              Next-Gen AI Communication
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-foreground mb-4 cursor-default group"
            >
              <span className="block transition-transform duration-300 group-hover:skew-x-2 group-hover:scale-[1.02]">
                Elevate Your{" "}
                <em className="text-gradient-pulse not-italic">Communication</em>
                <br />
                With Pulse Chat
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="font-body text-foreground-muted leading-relaxed max-w-md mb-8"
            >
              Pulse Chat delivers intelligent, automated messaging solutions that bridge the gap between businesses and customers, powered by state-of-the-art AI.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex flex-wrap gap-4"
            >
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="#projects"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-pulse text-primary-foreground font-body font-medium text-sm tracking-wide shadow-pulse"
              >
                Our Solutions
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.05, borderColor: "hsl(var(--pulse))", color: "hsl(var(--pulse))" }}
                whileTap={{ scale: 0.95 }}
                href="#contact"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-foreground/30 text-foreground font-body text-sm tracking-wide transition-all duration-300"
              >
                Contact Sales
              </motion.a>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="flex gap-10 mt-10 pt-8 border-t border-border/50"
            >
              {[
                { number: "PulseChat.ai", label: "Example Domain" },
                { number: "99.9%", label: "Accuracy" },
                { number: "24/7", label: "Intelligent Support" },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="font-display text-2xl font-semibold text-gradient-pulse">{stat.number}</p>
                  <p className="font-body text-xs text-foreground-muted mt-1 tracking-wide">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Photo with 3D Tilt */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
              rotateX,
              rotateY,
              transformStyle: "preserve-3d",
            }}
            className="order-1 md:order-2 flex justify-center md:justify-end perspective-1000"
          >
            <div className="relative group">
              {/* Pulse border frame */}
              <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-pulse/60 via-electric/60 to-transparent blur-sm group-hover:blur-md transition-all duration-500" />
              <div className="relative w-64 h-[350px] md:w-[380px] md:h-[480px] rounded-2xl overflow-hidden border border-pulse/20 shadow-card bg-space flex items-center justify-center">
                <motion.img
                  src={pulseHeroImg}
                  alt="Pulse Chat AI Vision"
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.6 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent" />
              </div>

              {/* Floating badge with motion - nudged further right/down to clear text completely */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-10 -right-16 glass-card rounded-xl px-5 py-3 shadow-card z-10"
              >
                <p className="font-body text-xs text-foreground-muted">System Status</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="w-2 h-2 rounded-full bg-primary animate-pulse shadow-[0_0_8px_hsl(var(--pulse))]" />
                  <span className="font-body text-sm text-foreground font-medium">Pulse Active</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ✅ Scroll indicator */}
      <motion.a
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        href="#about"
        className="pb-8 flex flex-col items-center gap-2 text-foreground-muted/60 hover:text-pulse transition-colors duration-300 group"
        aria-label="Scroll to About section"
      >
        <span className="font-body text-xs tracking-widest uppercase">Scroll</span>
        <ChevronDown
          size={18}
          className="animate-bounce"
        />
      </motion.a>
    </section>
  );
};

export default Hero;
